<template>
  <div
    v-if="error"
    class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
    role="alert"
    >
    <strong class="font-bold">Error! </strong>
    <span class="block sm:inline">{{ error }}</span>
  </div>
  <div v-if="canViewOrg || user?.is_staff" class="p-6">
    <!-- Header Section -->
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
        Organisations - {{ filteredOrgs.length }}
        <span v-if="isFiltered" class="text-sm text-gray-500">(filtered)</span>
      </h1>
      <button v-if="canAddOrg || user?.is_staff"
        @click="
          $router.push({ name: 'WorkSpaceOrganizationDetails', params: { id: 'new' } })
        "
        class="btn bg-blue-700 text-white hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
      >
        Add Organisation
      </button>
    </div>

    <!-- Search and Filter Section -->
    <div
      class="flex flex-col md:flex-row md:items-center md:justify-between mb-6"
    >
      <!-- Search Bar -->
      <div class="mb-4 md:mb-0 md:flex-1 md:mr-4">
        <SearchBar
          v-model="searchQuery"
          placeholderText="Search organisations by name..."
        />
      </div>

      <!-- Bulk Action Section -->
      <div class="flex items-center mt-4 md:mt-0 space-x-2">
        <GlassSelect
          v-model="bulkAction"
          :options="canDeleteOrg || user?.is_staff ? [{ value: 'delete', label: 'Delete selected items' }] : []"
          placeholder="Bulk Actions"
          :valueKey="'value'"
          :labelKey="'label'"
        />
        <button
          @click="applyBulkAction"
          :disabled="!bulkAction || selectedOrgs.length === 0"
          :class="[
            'btn focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200',
            bulkAction && selectedOrgs.length
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed',
          ]"
        >
          Apply
        </button>
      </div>
    </div>


    <GenericTable
      ref="table"
      :columns="columns"
      :items="paginatedOrgs"
      itemKey="id"
      :selectable="true"
      @update:selectedItems="updateSelectedOrgs"
      @rowClick="goToOrgDetail"
    >
      <!-- Custom cell templates -->
      <template #created_at="{ value }">
        {{ formatDateTime(value) }}
      </template>
    </GenericTable>

    <!-- Pagination -->
    <div
      class="flex flex-col md:flex-row items-center justify-between mt-8 space-y-4 md:space-y-0"
    >
      <div class="flex items-center space-x-2">
        <button
          @click="changePage(-1)"
          :disabled="currentPage === 1"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Previous
        </button>
        <button
          @click="changePage(1)"
          :disabled="currentPage === totalPages"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Next
        </button>
      </div>
      <div class="text-gray-700 dark:text-white">
        Page {{ currentPage }} of {{ totalPages }}
      </div>
      <div>
        <GlassSelect
          v-model="itemsPerPage"
          :options="[{ value: 10, label: '10 per page' }, { value: 20, label: '20 per page' }, { value: 50, label: '50 per page' }, { value: 100, label: '100 per page' }, { value: -1, label: 'All' }]"
          :valueKey="'value'"
          :labelKey="'label'"
          placeholder="Items per page"
          @change="updatePagination"
        />
      </div>
    </div>

    <DeleteModal
      v-if="deletingItem"
      :deleting-item="deletingItem"
      :componet-objects="componetObjects"
      :is-deleting="isDeleting"
      @delete="confirmDelete"
      @close="deletingItem=false"
    />
  </div>
</template>

<script>
import { getOrgs } from "@/services/wsOrgService"; 
import { getAgentGroups } from "@/services/agentGroupService";
import { getConfigs } from "@/services/configService";
import { getAgents } from "@/services/agentService";
import { getBinarys } from "@/services/binaryService";
import { getRateLimitConfigs } from "@/services/wsRateLimitSevice";
import SearchBar from "@/components/SearchBar.vue";
import GenericTable from "@/components/GenericTable.vue";
import { useStore } from "@/store";
import { deletewsOrg } from "@/services/wsOrgService";
import DeleteModal from "@/components/DeleteModal.vue";
import GlassSelect from "@/components/GlassSelect.vue";

export default {
  name: "OrganizationView",
  components: {
    SearchBar,
    GenericTable,
    DeleteModal,
    GlassSelect,
  },
  data() {
    return {
      orgs: [],
      searchQuery: "",
      filteredOrgs: [],
      currentPage: 1,
      itemsPerPage: 10,
      selectedOrgs: [],
      deletingItem: false,
      bulkAction: "",
      columns: [
        { key: "name", label: "Name", sortable: true, width: "25%" },
        {
          key: "org_key",
          label: "Organisation Key",
          sortable: true,
          width: "20%",
        },
      ],
      canViewOrg: false,
      canAddOrg: false,
      canDeleteOrg: false,
      error: "",
      isDeleting: false,
    };
  },
  computed: {
    user() {
      return useStore.getUser();
    },
    isFiltered() {
      return this.searchQuery !== "";
    },
    totalPages() {
      return this.itemsPerPage === -1
        ? 1
        : Math.ceil(this.filteredOrgs.length / this.itemsPerPage);
    },
    paginatedOrgs() {
      if (this.itemsPerPage === -1) {
        return this.filteredOrgs;
      }
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredOrgs.slice(start, end);
    },
    componetObjects() {
      return this.selectedOrgs.map(org => {
        return {
          values: [
            { name: "Organisation", value: org.name },
            { name: "Agent Groups", value: this.agentgroups.filter(item => item.organization == org.id).length},
            { name: "Configs", value: this.configs.filter(item => item.organization == org.id).length},
            { name: "Agents", value: this.agents.filter(item => item.organization == org.id).length},
            { name: "Binaries", value: this.binarys.filter(item => item.organization == org.id).length},
            { name: "Rate Limit Configurations", value: this.ratelimits.filter(item => item.organization == org.id).length},
          ]
        }
      })
    },
  },
  watch: {
    user: {
      handler(user) {
        if (user) {
          this.checkPermissions();
        }
      },
      immediate: true
    },
    searchQuery() {
      this.applyFilters();
    },
  },
  async mounted() {
    this.checkPermissions();
    await this.waitForStoreReady() 
    await this.loadData();
  },
  methods: {
    async waitForStoreReady() {
      const maxAttempts = 50;
      let attempts = 0;
      
      while (!useStore.userOrg() && attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }
    },
    async loadData(){
      await this.fetchOrganizations();
      await this.fetchRelatedItems();
    },
    async fetchRelatedItems(){
      this.agentgroups =  await getAgentGroups();
      this.agents = await getAgents();
      this.configs = await getConfigs();
      this.binarys = await getBinarys();
      this.ratelimits = await getRateLimitConfigs();
    },
    async checkPermissions() {
      if (this.user) {
        this.canAddOrg = await useStore.hasPermission("Can add organisation");
        this.canDeleteOrg = await useStore.hasPermission("Can delete organisation");
        this.canViewOrg = await useStore.hasPermission("Can view organisation");
      }
    },
    
    async fetchOrganizations() {
      try {
        this.orgs = await getOrgs();
        this.applyFilters();
      } catch (error) {
        console.error("Error fetching organizations:", error);
        if (error.response) {
          switch (error.response.status) {
            case 404:
              this.error = error.response.status +" The resource was not found. Please check the API endpoint."
              break;
            case 401:
              this.error = error.response.status + " Unauthorized access. Please check your authentication token or log in again."
              break;
            case 403:
              this.error = error.response.status + " You don't have permission to access this resource."
                          + " Contact a staff member to assign you the permissions to access this page. ";
              break;
            default:
              this.error = error.response.status =   error.response.data.detail || "Unknown error"
          }
        } else if (error.request) {
          alert(
            "No response received from the server. Please check your internet connection."
          );
        } else {
          alert(`An error occurred: ${error.message}`);
        }
      }
    },
    goToOrgDetail(org) {
      this.$router.push({
        name: "WorkSpaceOrganizationDetails",
        params: { id: org.id },
      });
    },
    updateSelectedOrgs(selectedItems) {
      this.selectedOrgs = selectedItems;
      // if (this.selectedOrgs) this.fetchRelatedModules();
    },
    async applyBulkAction() {
      if (this.bulkAction === "delete" ) {
        this.deletingItem = true;
      }
      this.bulkAction = "";
    },
    async confirmDelete() {
      this.isDeleting = true;
      await this.deleteSelectedOrgs();
      this.deletingItem = false; 
      this.bulkAction = "";
    },
    async deleteSelectedOrgs() {
      if (this.selectedOrgs.length === 0) {
        alert("No organizations selected for deletion");
        return;
      }

      try {
        const deletePromises = this.selectedOrgs.map((org) =>
          deletewsOrg(org.id)
        );
        await Promise.all(deletePromises);

        this.orgs = this.orgs.filter(
          (org) => !this.selectedOrgs.some((item) => item.id === org.id)
        );
        this.$refs.table.clearSelection();
        this.applyFilters();
        this.selectedOrgs = [];
      } catch (error) {
        console.error("Error during bulk delete:", error);
        alert(
          "An error occurred while deleting organizations. Please try again."
        );
      } finally {
        this.isDeleting = false;
      }
    },
    formatDateTime(dateString) {
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZoneName: "short",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    },
    applyFilters() {
      this.filteredOrgs = this.orgs.filter(
        (org) =>
          org?.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          org?.org_key
            .toLowerCase()
            .includes(this.searchQuery.toLowerCase())
      );
      this.currentPage = 1;
    },
    changePage(delta) {
      this.currentPage += delta;
    },
    updatePagination() {
      this.currentPage = 1;
    },
  },
};
</script>
